import React, { useState } from "react";
import { Modal, Button, Col, Row, Form } from "@themesberg/react-bootstrap";
import API from "../../common/API";
import { useFormik } from "formik";
import * as yup from "yup";
import { parsedUser } from "../../common/GetCurrentUser";
import { v4 as uuidv4 } from "uuid";

// ========================================
// VALIDATION SCHEMA
// ========================================
const validationSchema = yup.object({
  // No validation needed since folder name comes from selection
});

export default function UploadFolder(props) {
  // ========================================
  // STATE DECLARATIONS
  // ========================================
  const [state, setState] = useState({
    Model: props.Model
      ? props.Model
      : {
          name: "",
          userAccess: "",
          organizationId: "",
          parentId: null,
          projectId: null,
          created: new Date(),
          id: "",
        },
    IsLoading: false,
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    currentFile: "",
    percentage: 0,
    isUploading: false,
  });

  // ========================================
  // FORMIK CONFIGURATION
  // ========================================
  const formik = useFormik({
    initialValues: {},
    validationSchema,
    onSubmit: handleSubmit,
  });

  // ========================================
  // HELPER METHODS
  // ========================================
  const readDirectory = async (dirHandle, path = "") => {
    const files = [];
    for await (const [name, handle] of dirHandle.entries()) {
      const currentPath = path ? `${path}/${name}` : name;

      if (handle.kind === "file") {
        const file = await handle.getFile();
        files.push({
          name: file.name,
          path: currentPath,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          file: file,
        });
      } else if (handle.kind === "directory") {
        const subFiles = await readDirectory(handle, currentPath);
        files.push(...subFiles);
      }
    }
    return files;
  };

  const processWebkitFiles = (files) => {
    return files.map((file) => {
      // Get relative path by removing the first folder name
      const pathParts = file.webkitRelativePath.split("/");
      const relativePath = pathParts.slice(1).join("/") || file.name;

      return {
        name: file.name,
        path: relativePath,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        file: file,
        webkitRelativePath: file.webkitRelativePath,
      };
    });
  };

  const updateFolderName = (folderName) => {
    setSelectedFolderName(folderName);
  };

  // create the folder structure based on selected files
  const createFolderStructure = async (
    folderName,
    projectId,
    selectedFiles
  ) => {
    try {
      // Extract unique directory paths from selected files
      const uniquePaths = [
        ...new Set(
          selectedFiles
            .map((file) => file.path)
            .filter((path) => path.includes("/")) // Only files in subdirectories
            .map((path) => path.substring(0, path.lastIndexOf("/"))) // Get directory path
        ),
      ];

      // Create the folder structure payload
      const folderStructure = {
        rootFolderName: folderName,
        projectId: projectId,
        paths: uniquePaths.sort(), // Sort to create parent folders first
        totalFiles: selectedFiles.length,
        totalSize: selectedFiles.reduce((sum, file) => sum + file.size, 0),
      };

      const theUser = parsedUser();
      const query = {
        Id: theUser.ID,
        ProjectID: props.selectedProject,
        Data: folderStructure,
      };

      const response = await API.postAction(
        "File/CreateFolderStructure",
        query
      );

      if (response.data.status === "ok") {
        return {
          success: true,
          rootFolderId: response.data.response.rootFolderId,
          folderMap: response.data.response.folderMap, // Maps path -> folderId
          message: response.data.response.message,
        };
      } else {
        throw new Error(
          response.data.message || "Error creating folder structure"
        );
      }
    } catch (error) {
      console.error("Error creating folder structure:", error);
      return {
        success: false,
        error: error.message || "Unknown error creating folders",
      };
    }
  };

  // Helper function to get folder ID for a file based on its path
  const getFolderIdForFile = (fileObj, folderMap) => {
    // If file is in root folder
    if (!fileObj.path.includes("/")) {
      return folderMap[""]; // Root folder ID
    }

    // Get the directory path for the file
    const dirPath = fileObj.path.substring(0, fileObj.path.lastIndexOf("/"));
    return folderMap[dirPath] || folderMap[""];
  };

  // Create file metadata using existing pattern
  const createFileMetadata = async (fileObj, folderId) => {
    const theUser = parsedUser();
    const newItem = {
      id: uuidv4(),
      Name: fileObj.name,
      Note: "",
      Path: fileObj.name, // Use just the file name, not the full path
      Userid: theUser.id,
      Parentid: folderId,
      Type: "file",
      projectId: props.projectId,
      organizationId: theUser.OrganizationId,
    };

    const query = {
      Id: theUser.ID,
      Data: newItem,
    };

    return await API.postAction("File/AddEditFileWithoutFile", query);
  };

  // Upload file content using existing pattern - simplified
  const uploadFileContent = async (fileObj, fileId) => {
    const bodyData = new FormData();
    bodyData.append("path", fileObj.file); // Just append the file, like in AddAttachments

    return await API.postAction(`File/UploadFile?id=${fileId}`, bodyData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  // Upload all files using existing pattern (loop approach)
  const uploadFiles = async (folderMap, selectedFiles) => {
    const totalFiles = selectedFiles.length;
    let uploadedFiles = 0;

    for (const fileObj of selectedFiles) {
      try {
        // Update progress
        setUploadProgress({
          current: uploadedFiles + 1,
          total: totalFiles,
          currentFile: fileObj.name,
          percentage: Math.round(((uploadedFiles + 1) / totalFiles) * 100),
          isUploading: true,
        });

        // Get folder ID for this file
        const folderId = getFolderIdForFile(fileObj, folderMap);

        // Step 1: Create file metadata
        const metadataResponse = await createFileMetadata(fileObj, folderId);

        if (metadataResponse.data.status === "ok") {
          const responseGeneral = metadataResponse.data.response;

          // Step 2: Upload the actual file now (matching AddAttachments pattern)
          await uploadFileContent(fileObj, responseGeneral.id);

          uploadedFiles++;
        }
      } catch (error) {
        console.error(`Error uploading ${fileObj.name}:`, error);
        // Continue with next file
      }
    }

    // Mark upload as complete
    setUploadProgress((prev) => ({
      ...prev,
      isUploading: false,
    }));
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSelectFolder = async () => {
    // Try modern File System Access API first (Chrome 86+)
    if (window.showDirectoryPicker) {
      try {
        const directoryHandle = await window.showDirectoryPicker();
        const files = await readDirectory(directoryHandle);

        setSelectedFiles(files);
        updateFolderName(directoryHandle.name);
        return;
      } catch (error) {
        if (error.name === "AbortError") {
          return; // User cancelled
        }
        console.error("Error with showDirectoryPicker:", error);
        // Fall back to webkitdirectory
      }
    }

    // Fallback to webkitdirectory (broader browser support)
    const input = document.createElement("input");
    input.type = "file";
    input.webkitdirectory = true;
    input.multiple = true;
    input.style.display = "none";

    input.onchange = (event) => {
      const files = Array.from(event.target.files);

      if (files.length === 0) return;

      const processedFiles = processWebkitFiles(files);
      setSelectedFiles(processedFiles);

      // Update folder name
      if (files.length > 0) {
        const folderName = files[0].webkitRelativePath.split("/")[0];
        updateFolderName(folderName);
      }
    };

    // Trigger the file picker
    input.click();
  };

  async function handleSubmit() {
    try {
      setState({
        ...state,
        IsLoading: true,
      });

      // Validate that we have a folder selected
      if (!selectedFolderName || selectedFiles.length === 0) {
        throw new Error("Por favor selecciona una carpeta con archivos");
      }

      // Step 1: Create folder structure
      const folderResult = await createFolderStructure(
        selectedFolderName,
        state.Model.projectId || props.projectId,
        selectedFiles
      );

      if (!folderResult.success) {
        throw new Error(folderResult.error);
      }

      // Step 2: Upload files using existing pattern
      await uploadFiles(folderResult.folderMap, selectedFiles);

      // Return success result
      props.toggle(folderResult, true);
    } catch (ex) {
      console.error("Error in handleSubmit:", ex);
      // You might want to show an error message to the user here
      alert(`Error: ${ex.message}`);
    } finally {
      setState({
        ...state,
        IsLoading: false,
      });
    }
  }

  // ========================================
  // SIDE EFFECTS
  // ========================================
  // No side effects needed for this component

  // ========================================
  // RENDER
  // ========================================
  return (
    <Modal show={true} onHide={props.toggle} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Subir Carpeta</Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          {/* Selected Folder Display */}
          {selectedFolderName && (
            <Row className="mb-3">
              <Col xs={12}>
                <div className="alert alert-info">
                  <strong>Carpeta seleccionada:</strong> {selectedFolderName}
                </div>
              </Col>
            </Row>
          )}

          {/* Folder Selection Button */}
          <Row>
            <Col xs={12} md={12}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSelectFolder}
              >
                <span role="img" aria-label="folder">
                  📁
                </span>{" "}
                Seleccionar carpeta y archivos
              </button>
              <small className="form-text text-muted d-block mt-1">
                Se seleccionarán todos los archivos dentro de la carpeta
              </small>
            </Col>
          </Row>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <Row className="mt-3">
              <Col xs={12}>
                <h6>Archivos seleccionados ({selectedFiles.length}):</h6>
                <hr />

                {/* Upload Progress */}
                {uploadProgress.isUploading && (
                  <div className="upload-progress mb-3">
                    <div className="progress mb-2">
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{ width: `${uploadProgress.percentage}%` }}
                      />
                    </div>
                    <small className="text-muted">
                      Subiendo: <strong>{uploadProgress.currentFile}</strong>(
                      {uploadProgress.current}/{uploadProgress.total}) -{" "}
                      {uploadProgress.percentage}%
                    </small>
                  </div>
                )}

                <div style={{ maxHeight: "100%", overflowY: "auto" }}>
                  <ol>
                    {selectedFiles.map((file, index) => (
                      <li key={index}>
                        <div>
                          <span className="text-primary">{file.name}</span>
                          <small className="text-muted ms-2">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </small>
                        </div>
                        <div>
                          <small className="text-muted">{file.path}</small>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            type="submit"
            className="btn-sm"
            disabled={
              state.IsLoading ||
              uploadProgress.isUploading ||
              selectedFiles.length === 0 ||
              !selectedFolderName
            }
          >
            {state.IsLoading && !uploadProgress.isUploading
              ? "Creando estructura..."
              : uploadProgress.isUploading
              ? `Subiendo archivos... (${uploadProgress.current}/${uploadProgress.total})`
              : "Subir Carpeta"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
